import { useEffect, useState } from 'react';
import {
  useAtualizarSocio, useConfigFinanceiro, useExcluirSocio, useSalvarConfigFinanceiro,
  useSocios, useUsuarios,
} from '../../hooks';
import type { Socio } from '../../types';
import { fmtPct } from './formatadores';
import { FormSocio } from './FormSocio';

const inp = 'text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400';

/** Seção da Administração (admin-only): % do escritório + cadastro/divisão padrão dos sócios. */
export function SeccaoSociosHonorarios({ cardCls }: { cardCls: string }) {
  const { data: config } = useConfigFinanceiro();
  const { data: socios = [] } = useSocios();
  const { data: usuarios = [] } = useUsuarios();
  const salvarConfig = useSalvarConfigFinanceiro();

  const [pct, setPct] = useState(0);
  useEffect(() => { if (config) setPct(config.pct_contratuais_padrao); }, [config]);
  const configDirty = !!config && Math.abs(pct - config.pct_contratuais_padrao) > 0.001;

  const somaC = socios.filter((s) => s.ativo).reduce((a, b) => a + b.pct_contratuais, 0);
  const somaS = socios.filter((s) => s.ativo).reduce((a, b) => a + b.pct_sucumbencia, 0);
  const okC = Math.abs(somaC - 100) < 0.01;
  const okS = Math.abs(somaS - 100) < 0.01;

  return (
    <div className={cardCls}>
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">💼 Sócios &amp; honorários</h2>

      {/* % de honorários contratuais do escritório */}
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-0.5">% de honorários contratuais do escritório</label>
          <input type="number" min={0} max={100} step="0.01" value={pct}
            onChange={(e) => setPct(parseFloat(e.target.value) || 0)} className={`${inp} w-28 text-right tabular-nums`} />
        </div>
        <button type="button" disabled={!configDirty || salvarConfig.isPending}
          onClick={() => salvarConfig.mutate(pct)}
          className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
          Salvar %
        </button>
        <span className="text-[11px] text-gray-400">aplicado como padrão em novas receitas</span>
      </div>

      {/* Tabela de sócios */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium">Sócio</th>
              <th className="px-2 py-1.5 font-medium w-24">% contrat.</th>
              <th className="px-2 py-1.5 font-medium w-24">% sucumb.</th>
              <th className="text-center px-2 py-1.5 font-medium">Ativo</th>
              <th className="px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {socios.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-4 text-center text-xs text-gray-400">Nenhum sócio cadastrado ainda.</td></tr>
            )}
            {socios.map((s) => <LinhaSocio key={s.id} socio={s} />)}
          </tbody>
          {socios.length > 0 && (
            <tfoot>
              <tr className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                <td className="px-2 py-1.5 text-xs text-gray-500">Soma (ativos)</td>
                <td className={`px-2 py-1.5 text-center text-xs font-semibold ${okC ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{fmtPct(somaC)} {okC ? '✓' : '⚠'}</td>
                <td className={`px-2 py-1.5 text-center text-xs font-semibold ${okS ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{fmtPct(somaS)} {okS ? '✓' : '⚠'}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {(!okC || !okS) && socios.length > 0 && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400">Dica: as somas idealmente fecham 100% — fora disso, a divisão das novas receitas não soma o total.</p>
      )}

      <FormSocio usuarios={usuarios} />
    </div>
  );
}

function LinhaSocio({ socio }: { socio: Socio }) {
  const [nome, setNome] = useState(socio.nome);
  const [pctC, setPctC] = useState(socio.pct_contratuais);
  const [pctS, setPctS] = useState(socio.pct_sucumbencia);
  useEffect(() => { setNome(socio.nome); setPctC(socio.pct_contratuais); setPctS(socio.pct_sucumbencia); }, [socio]);
  const atualizar = useAtualizarSocio();
  const excluir = useExcluirSocio();
  const dirty = nome !== socio.nome || pctC !== socio.pct_contratuais || pctS !== socio.pct_sucumbencia;

  function salvar() {
    atualizar.mutate({ id: socio.id, body: { nome: nome.trim(), pct_contratuais: pctC, pct_sucumbencia: pctS } });
  }

  return (
    <tr className="border-t border-gray-100 dark:border-gray-800">
      <td className="px-2 py-1">
        <input value={nome} onChange={(e) => setNome(e.target.value)} className={`${inp} w-full`} />
      </td>
      <td className="px-2 py-1">
        <input type="number" min={0} max={100} step="0.01" value={pctC} onChange={(e) => setPctC(parseFloat(e.target.value) || 0)} className={`${inp} w-full text-right tabular-nums`} />
      </td>
      <td className="px-2 py-1">
        <input type="number" min={0} max={100} step="0.01" value={pctS} onChange={(e) => setPctS(parseFloat(e.target.value) || 0)} className={`${inp} w-full text-right tabular-nums`} />
      </td>
      <td className="px-2 py-1 text-center">
        <input type="checkbox" checked={socio.ativo}
          onChange={(e) => atualizar.mutate({ id: socio.id, body: { ativo: e.target.checked } })}
          className="h-4 w-4 accent-blue-600" />
      </td>
      <td className="px-2 py-1 text-right whitespace-nowrap">
        {dirty && (
          <button onClick={salvar} disabled={atualizar.isPending}
            className="text-xs px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700 mr-1">salvar</button>
        )}
        <button onClick={() => excluir.mutate(socio.id)} title="Desativar" className="text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 px-1">🗑</button>
      </td>
    </tr>
  );
}
